// WorkspaceHome: the workspace mode's main column: the workspaces being
// worked in, side by side (WorkspaceDeck), with the sheet of what there is to
// bring onto them over them, and the chat docked beside. The sessions it is
// opened from are the mode's sidebar (WorkspaceThreads), as a chat harness
// keeps its threads beside the one open; a session dragged from there onto a
// workspace is laid on it.
//
// Whatever opens a workspace (lib/traceCanvasStore.js: a chat's
// investigation, a case) opens it in a pane; the board the reader works in is
// the store's open canvas, so the chat's workspace tools read and lay on it.
// A session clicked (in the sidebar, on a Triage row's card, a trace's
// Workspace button) is previewed over the panes first: a workspace of its own
// while it is up, the chat in its dock; its Mount opens it in the pane
// worked in. `?fleet=mode` shows the fleet's old panes (FleetCanvas)
// instead.
import { useCallback, useEffect, useState } from "react";
import { useFreshChatOnRequest } from "../components/CanvasChat.jsx";
import FleetCanvas from "../components/FleetCanvas.jsx";
import TraceCanvasOverlay from "../components/TraceCanvasOverlay.jsx";
import WorkspaceDeck from "../components/WorkspaceDeck.jsx";
import WorkspaceDock from "../components/WorkspaceDock.jsx";
import { SESSION_DRAG_TYPE } from "../components/WorkspaceThreads.jsx";
import { requestWorkspaceFocus } from "../lib/workspaceStore.js";
import { showWorkspaceInDeck } from "../lib/deckRequests.js";
import { closeTracePreview, layOnTraceCanvas, openTraceCanvas, traceCanvasOpen, useTraceCanvasOpen } from "../lib/traceCanvasStore.js";

// Read where it renders: a load comes through the sign-in callback first.
const fleetMode = () => new URLSearchParams(globalThis.location?.search || "").get("fleet") === "mode";
const draggingSession = (e) => [...(e.dataTransfer?.types || [])].includes(SESSION_DRAG_TYPE);
// Inside its frames the workspace stands on its own ground, a step off the
// page's: the board, its dock and the chat in it draw on it. The gutters
// around and between the frames keep the page's, so each frame is the one
// edge between the two.
const WORKSPACE_GROUND = { "--bg": "var(--workspace-bg)", "--plot-bg": "var(--workspace-bg)" };

// With nothing open there is no board to draw yet: the panes take the room,
// and the chat sits in the dock beside them (WorkspaceDock), as a
// workspace's does. The app's one console docks here; a session previewed
// over the panes has the same dock of its own while it is up, the console in
// that one.
function EmptyBoard({ range, onRangeChange = null }) {
  useFreshChatOnRequest();
  // The chat in the dock is the workspace's in the pane worked in, not
  // whichever the console had in front elsewhere (the dashboard's).
  const open = useTraceCanvasOpen();
  return (
    <WorkspaceDock scope="home" chatFor={!open ? null : open.inDeck ? open.id : undefined} behind={open?.preview ? open.id : null} opened={open?.id ?? null} data-workspace-home-empty="">
      <WorkspaceDeck range={range} onRangeChange={onRangeChange} />
    </WorkspaceDock>
  );
}

export default function WorkspaceHome({ range = "1M", onRangeChange = null, onFind = null }) {
  // Coming back to the home comes back to its panes as they were left (the
  // deck keeps them), not to the last workspace's board over the page.
  const open = useTraceCanvasOpen();
  const [dropping, setDropping] = useState(false);
  // Every workspace opens in a pane: one opened from anywhere else (a chat's
  // investigation, a case) goes to the deck, with what its opener holds for
  // its board. A session previewed stays over the panes.
  useEffect(() => {
    if (!open || open.inDeck || open.preview || fleetMode()) return;
    showWorkspaceInDeck(open.id, { trace: open.trace || null, focus: open.focus || null });
  }, [open]);
  // A preview left up is put away with the home, not found over it again.
  useEffect(() => () => closeTracePreview(), []);

  // A session dropped on the board goes beside what it holds (or starts a
  // workspace on it, with none open).
  // The deck's panes take what is dropped on them; the fleet's old panes
  // leave the rest to the page.
  const drops = fleetMode();
  const lay = useCallback((entry) => {
    const cur = traceCanvasOpen();
    if (!cur) {
      openTraceCanvas({ id: entry.id, title: entry.title });
      return;
    }
    layOnTraceCanvas(cur.id, entry);
    requestWorkspaceFocus(entry.id);
  }, []);

  return (
    <div
      className="relative h-full min-h-0 min-w-0 bg-[color:var(--bg)] pb-[10px] pr-[10px] pt-[11px]"
      onDragOver={(e) => {
        if (!drops || !draggingSession(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        if (!dropping) setDropping(true);
      }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDropping(false); }}
      onDrop={(e) => {
        if (!drops || !draggingSession(e)) return;
        e.preventDefault();
        setDropping(false);
        // A workspace's board in a pane laid it on itself.
        if (e.nativeEvent?.laidOn) return;
        try {
          const entry = JSON.parse(e.dataTransfer.getData(SESSION_DRAG_TYPE) || "null");
          if (entry?.id) lay(entry);
        } catch { /* not a session */ }
      }}
      data-workspace-home=""
      data-workspace-paper=""
    >
      <div className="h-full min-h-0 min-w-0" style={WORKSPACE_GROUND} data-workspace-ground="">
        {fleetMode() ? <FleetCanvas range={range} onRangeChange={onRangeChange} onFind={onFind} /> : <EmptyBoard range={range} onRangeChange={onRangeChange} />}
      </div>
      {dropping ? (
        <div className="pointer-events-none absolute bottom-[10px] left-0 right-[10px] top-[11px] z-[30] grid place-items-center rounded-[2px] border border-dashed border-[color:var(--line-strong)] bg-[color:color-mix(in_srgb,var(--green-soft)_70%,transparent)]" data-workspace-drop="">
          <span className="rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] px-[10px] py-[5px] text-[12px] text-[color:var(--ink)] shadow-[var(--node-shadow-lift)]">
            {open ? "Drop to lay it on this workspace" : "Drop to start a workspace on it"}
          </span>
        </div>
      ) : null}
      {fleetMode() ? null : <TraceCanvasOverlay preview range={range} onRangeChange={onRangeChange} />}
    </div>
  );
}
